import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Page,
  Card,
  Text,
  Button,
  DataTable,
  Badge,
  Box,
  Popover,
  ActionList,
  Modal,
  Banner,
  Tabs,
  IndexTable,
  LegacyCard,
  DatePicker,
  TextField,
  Spinner,
  RadioButton,
  BlockStack,
  Select,
  InlineGrid,
  InlineStack,
  OptionList,
  Scrollable,
  useBreakpoints,
  ProgressBar,
  Tooltip as ToolTip,
  Toast,
} from "@shopify/polaris";
import {
  EditIcon,
  MagicIcon,
  MenuHorizontalIcon,
  CalendarIcon,
  ArrowRightIcon,
  ResetIcon,
  InfoIcon,
  XSmallIcon,
} from "@shopify/polaris-icons";
import { Icon } from "@shopify/polaris";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment-timezone";
import { format, parseISO } from "date-fns";
import useFreshworksWidget from "../hooks/useFreshworksWidget";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import ArticleModal from "../Modals/ArticleModal";
import DeleteArticleModal from "../Modals/DeleteArticleModal";
import UpdateStatusModal from "../Modals/UpdateStatusModal";
import GeneratedArticleModal from "../Modals/GeneratedArticleModal";
import OptimisedArticleModal from "../Modals/OptimisedArticleModal";
import ReviewManager, { useReviewTrigger } from "./ReviewManager";

// Helper function for formatting dates
function formatDateToYearMonthDayDateString(date) {
  const year = String(date.getFullYear());
  let month = String(date.getMonth() + 1);
  let day = String(date.getDate());
  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;
  return [year, month, day].join("-");
}

// Custom header for Polaris DatePicker (multiMonth)
function MonthHeader({ month, year, onPrev, onNext }) {
  const left = new Date(year, month, 1);
  const right = new Date(year, month + 1, 1);

  const leftLabel = left.toLocaleString("en-US", { month: "long" });
  const rightLabel = right.toLocaleString("en-US", { month: "long" });

  const leftYear = left.getFullYear();
  const rightYear = right.getFullYear();

  const yearDisplay =
    leftYear === rightYear ? leftYear : `${leftYear} – ${rightYear}`;

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            fontWeight: "600",
            marginBottom: "8px",
            color: "var(--p-color-text)",
          }}
        >
          {yearDisplay}
        </div>
      </div>
      <div className="dp-header" style={{ textAlign: "center" }}>
        <span className="dp-label">
          {leftLabel} - {rightLabel}
        </span>
      </div>
    </>
  );
}

function DateRangePicker({ initialDateRange, onChange }) {
  const { mdDown, lgUp } = useBreakpoints();
  const shouldShowMultiMonth = lgUp;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const ranges = [
    {
      title: "Last 7 days",
      alias: "last7days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 7)).setHours(0, 0, 0, 0)
        ),
        until: today,
      },
    },
    {
      title: "Last 30 days",
      alias: "last30days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 30)).setHours(
            0,
            0,
            0,
            0
          )
        ),
        until: today,
      },
    },
    {
      title: "Last 90 days",
      alias: "last90days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 90)).setHours(
            0,
            0,
            0,
            0
          )
        ),
        until: today,
      },
    },
  ];
  const [popoverActive, setPopoverActive] = useState(false);
  const [activeDateRange, setActiveDateRange] = useState(
    initialDateRange || ranges[1]
  );
  const [inputValues, setInputValues] = useState({});
  const [{ month, year }, setDate] = useState({
    month: activeDateRange.period.since.getMonth(),
    year: activeDateRange.period.since.getFullYear(),
  });

  const datePickerRef = useRef(null);
  const VALID_YYYY_MM_DD_DATE_REGEX = /^\d{4}-\d{1,2}-\d{1,2}/;

  useEffect(() => {
    if (popoverActive) {
      const timer = setTimeout(() => {
        const titles = document.querySelectorAll(".Polaris-DatePicker__Title");
        titles.forEach((title) => {
          const text = title.textContent.trim();
          if (/\s\d{4}$/.test(text)) {
            title.textContent = text.slice(0, -5);
          }
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [popoverActive, month, year]);

  function isDate(date) {
    return !isNaN(new Date(date).getDate());
  }

  function isValidYearMonthDayDateString(date) {
    return VALID_YYYY_MM_DD_DATE_REGEX.test(date) && isDate(date);
  }

  function isValidDate(date) {
    return date.length === 10 && isValidYearMonthDayDateString(date);
  }

  function parseYearMonthDayDateString(input) {
    const [year, month, day] = input.split("-");
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  function formatDate(date) {
    return formatDateToYearMonthDayDateString(date);
  }

  function handleStartInputValueChange(value) {
    setInputValues((prevState) => ({ ...prevState, since: value }));
    if (isValidDate(value)) {
      const newSince = parseYearMonthDayDateString(value);
      setActiveDateRange((prevState) => {
        const newPeriod =
          prevState.period && newSince <= prevState.period.until
            ? { since: newSince, until: prevState.period.until }
            : { since: newSince, until: newSince };
        return { ...prevState, period: newPeriod };
      });
    }
  }

  function handleEndInputValueChange(value) {
    setInputValues((prevState) => ({ ...prevState, until: value }));
    if (isValidDate(value)) {
      const newUntil = parseYearMonthDayDateString(value);
      setActiveDateRange((prevState) => {
        const newPeriod =
          prevState.period && newUntil >= prevState.period.since
            ? { since: prevState.period.since, until: newUntil }
            : { since: newUntil, until: newUntil };
        return { ...prevState, period: newPeriod };
      });
    }
  }

  function handleCalendarChange({ start, end }) {
    const newDateRange = ranges.find(
      (range) =>
        range.period.since.valueOf() === start.valueOf() &&
        range.period.until.valueOf() === end.valueOf()
    ) || {
      alias: "custom",
      title: "Custom Range",
      period: { since: start, until: end },
    };
    setActiveDateRange(newDateRange);
  }

  function apply() {
    if (onChange) onChange(activeDateRange.period);
    setPopoverActive(false);
  }

  function cancel() {
    setPopoverActive(false);
  }

  useEffect(() => {
    if (activeDateRange) {
      setInputValues({
        since: formatDate(activeDateRange.period.since),
        until: formatDate(activeDateRange.period.until),
      });
      function monthDiff(referenceDate, newDate) {
        return (
          newDate.month -
          referenceDate.month +
          12 * (referenceDate.year - newDate.year)
        );
      }
      const monthDifference = monthDiff(
        { year, month },
        {
          year: activeDateRange.period.until.getFullYear(),
          month: activeDateRange.period.until.getMonth(),
        }
      );
      if (monthDifference > 1 || monthDifference < 0) {
        setDate({
          month: activeDateRange.period.until.getMonth(),
          year: activeDateRange.period.until.getFullYear(),
        });
      }
    }
  }, [activeDateRange]);

  const buttonValue =
    activeDateRange.alias === "custom"
      ? `${activeDateRange.period.since.toDateString()} - ${activeDateRange.period.until.toDateString()}`
      : activeDateRange.title;

  return (
    <Popover
      active={popoverActive}
      autofocusTarget="none"
      preferredAlignment="left"
      preferredPosition="below"
      fluidContent
      sectioned={false}
      fullHeight
      activator={
        <Button
          size="slim"
          icon={CalendarIcon}
          onClick={() => setPopoverActive(!popoverActive)}
        >
          {buttonValue}
        </Button>
      }
      onClose={() => setPopoverActive(false)}
    >
      <Popover.Pane fixed>
        <InlineGrid
          columns={{ xs: "1fr", mdDown: "1fr", md: "max-content max-content" }}
          gap={0}
          ref={datePickerRef}
        >
          <Box
            maxWidth={mdDown ? "516px" : "212px"}
            width={mdDown ? "100%" : "212px"}
            padding={{ xs: 500, md: 0 }}
            paddingBlockEnd={{ xs: 100, md: 0 }}
          >
            {mdDown ? (
              <Select
                label="dateRangeLabel"
                labelHidden
                onChange={(value) => {
                  const result = ranges.find(
                    ({ title, alias }) => title === value || alias === value
                  );
                  setActiveDateRange(result);
                }}
                value={activeDateRange?.title || activeDateRange?.alias || ""}
                options={ranges.map(({ alias, title }) => title || alias)}
              />
            ) : (
              <Scrollable style={{ height: "334px" }}>
                <OptionList
                  options={ranges.map((range) => ({
                    value: range.alias,
                    label: range.title,
                  }))}
                  selected={[activeDateRange.alias]}
                  onChange={(value) => {
                    setActiveDateRange(
                      ranges.find((range) => range.alias === value[0])
                    );
                  }}
                />
              </Scrollable>
            )}
          </Box>
          <Box padding={{ xs: 500 }} maxWidth={mdDown ? "320px" : "516px"}>
            <BlockStack gap="400">
              <InlineStack gap="200">
                <div style={{ flexGrow: 1 }}>
                  <TextField
                    role="combobox"
                    label="Since"
                    labelHidden
                    prefix={<Icon source={CalendarIcon} />}
                    value={inputValues.since}
                    onChange={handleStartInputValueChange}
                    autoComplete="off"
                  />
                </div>
                <Icon source={ArrowRightIcon} />
                <div style={{ flexGrow: 1 }}>
                  <TextField
                    role="combobox"
                    label="Until"
                    labelHidden
                    prefix={<Icon source={CalendarIcon} />}
                    value={inputValues.until}
                    onChange={handleEndInputValueChange}
                    autoComplete="off"
                  />
                </div>
              </InlineStack>
              <div className="dp-with-header">
                <MonthHeader
                  month={month}
                  year={year}
                  onPrev={() => {
                    const prev = new Date(year, month - 1, 1);
                    setDate({
                      month: prev.getMonth(),
                      year: prev.getFullYear(),
                    });
                  }}
                  onNext={() => {
                    const next = new Date(year, month + 1, 1);
                    setDate({
                      month: next.getMonth(),
                      year: next.getFullYear(),
                    });
                  }}
                />
                <DatePicker
                  selected={{
                    start: activeDateRange.period.since,
                    end: activeDateRange.period.until,
                  }}
                  onChange={handleCalendarChange}
                  allowRange
                  month={month}
                  year={year}
                  multiMonth
                  onMonthChange={(m, y) => setDate({ month: m, year: y })}
                />
              </div>
            </BlockStack>
          </Box>
        </InlineGrid>
      </Popover.Pane>
      <Popover.Pane fixed>
        <Popover.Section>
          <InlineStack align="end">
            <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
              <Button onClick={cancel}>Cancel</Button>
              <Button variant="primary" onClick={apply}>
                Apply
              </Button>
            </div>
          </InlineStack>
        </Popover.Section>
      </Popover.Pane>
    </Popover>
  );
}

function SortableCard({
  id,
  editMode,
  children,
  className,
  style: customStyle,
  draggingHeight,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !editMode });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition: editMode && !isDragging ? transition : undefined,
    zIndex: isDragging ? 100 : "auto",
    background: "white",
    borderRadius: "8px",
    position: "relative",
    opacity: isDragging ? 0.5 : 1,
    height:
      isDragging && draggingHeight[id] ? `${draggingHeight[id]}px` : undefined,
    ...customStyle,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sortable-card ${editMode ? "is-edit" : ""} ${className}`}
      data-dragging={isDragging}
      data-id={id}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

function Dashboard({ shopOrigin, apiKey }) {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  // initLoading: only used to show skeleton rows in the Recent Posts table.
  // The Page shell renders immediately — no full-screen spinner.
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState({
    show: false,
    message: "",
    status: "success",
  });
  const [selectedTab, setSelectedTab] = useState(0);
  const [activePopoverId, setActivePopoverId] = useState(null);
  const [blogCounts, setBlogCounts] = useState({
    total: 0,
    published: 0,
    unpublished: 0,
  });
  const [blogTableCounts, setBlogTableCounts] = useState({
    total: 0,
    published: 0,
    unpublished: 0,
  });
  const [selectedStatus, setSelectedStatus] = useState("published");
  const [showPromoBanner, setShowPromoBanner] = useState(() => {
    try {
      return localStorage.getItem("ai_promo_banner_dismissed") !== "true";
    } catch {
      return true;
    }
  });

  const [topPostsBySessions, setTopPostsBySessions] = useState([]);
  const [topPostsByEngagement, setTopPostsByEngagement] = useState([]);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [engagementLoading, setEngagementLoading] = useState(false);

  const [topClickedProducts, setTopClickedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const [insights, setInsights] = useState({
    performance_insights: [],
    content_optimization: [],
    strategic_actions: [],
  });
  const [insightsLoading, setInsightsLoading] = useState(false);

  const [publishedThisWeek, setPublishedThisWeek] = useState(0);
  const [totalThisWeek, setTotalThisWeek] = useState(0);

  const [dateRangeSessions, setDateRangeSessions] = useState({
    since: new Date(new Date().setDate(new Date().getDate() - 30)),
    until: new Date(),
  });
  const [dateRangeEngagement, setDateRangeEngagement] = useState({
    since: new Date(new Date().setDate(new Date().getDate() - 30)),
    until: new Date(),
  });
  const [dateRangeProducts, setDateRangeProducts] = useState({
    since: new Date(new Date().setDate(new Date().getDate() - 30)),
    until: new Date(),
  });
  const [dateRangeCharts, setDateRangeCharts] = useState({
    since: new Date(new Date().setDate(new Date().getDate() - 30)),
    until: new Date(),
  });
  const [chartData, setChartData] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(false);

  const { openWidget } = useFreshworksWidget(206000000367, true);

  const [shopOwnerName, setShopOwnerName] = useState("");

  const [editMode, setEditMode] = useState(false);

  const [processingArticleId, setProcessingArticleId] = useState(null);
  const [processingType, setProcessingType] = useState("");

  const [topPostsSessionsCount, setTopPostsSessionsCount] = useState("5");
  const [topPostsEngagementsCount, setTopPostsEngagementsCount] = useState("5");
  const [topPostsProductsCount, setTopPostsProductsCount] = useState("5");

  const totalCount = blogCounts.total;

  const [generatedArticle, setGeneratedArticle] = useState(null);
  const [assistantInstruction, setAssistantInstruction] = useState("");

  // Optimised article preview modal state
  const [optimisedArticleModalOpen, setOptimisedArticleModalOpen] = useState(false);
  const [optimisedArticlePreview, setOptimisedArticlePreview] = useState(null); // { article_title, article_body, article_id, status, blog_id }
  const [isConfirmingOptimise, setIsConfirmingOptimise] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingMessage, setProcessingMessage] = useState("");

  const [chartTypePopoverActive, setChartTypePopoverActive] = useState(false);
  const [chartViewPopoverActive, setChartViewPopoverActive] = useState(false);
  const [sessionsCountPopoverActive, setSessionsCountPopoverActive] =
    useState(false);
  const [engagementCountPopoverActive, setEngagementCountPopoverActive] =
    useState(false);

  const [layoutLoading, setLayoutLoading] = useState(true);
  const [gridLayout, setGridLayout] = useState([]);

  const [toastActive, setToastActive] = useState(false);
  const [toastContent, setToastContent] = useState("");
  const [toastError, setToastError] = useState(false);

  const showToast = (message, isError = false) => {
    setToastContent(message);
    setToastError(isError);
    setToastActive(true);
  };

  const [reviewTrigger, setReviewTrigger] = useState(null);
  const triggerReview = useReviewTrigger(shopOrigin);

  const simulateProgress = (targetProgress = 98, duration = 30000) => {
    const startTime = Date.now();
    const updateInterval = 100;

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progressRatio = Math.min(elapsed / duration, 1);

      const easedProgress = 1 - Math.pow(1 - progressRatio, 3);
      const currentProgress = Math.floor(easedProgress * targetProgress);

      setProcessingProgress((prev) => {
        if (prev >= targetProgress) {
          clearInterval(interval);
          return prev;
        }
        return currentProgress;
      });
    }, updateInterval);

    return interval;
  };

  const fetchLayout = useCallback(async () => {
    if (!shopOrigin) return;
    setLayoutLoading(true);
    try {
      const response = await axios.get(`/api/get-layout?shop=${shopOrigin}`);
      const savedLayout = response.data.layout;
      const defaultLayout = getDefaultGridLayout();

      if (savedLayout && savedLayout.length > 0) {
        setGridLayout(calculateRowLayout(savedLayout));
      } else {
        setGridLayout(calculateRowLayout(defaultLayout));
      }
    } catch (error) {
      console.error("Error fetching layout:", error);
      const defaultLayout = getDefaultGridLayout();
      setGridLayout(calculateRowLayout(defaultLayout));
    } finally {
      setLayoutLoading(false);
    }
  }, [shopOrigin]);

  useEffect(() => {
    fetchLayout();
  }, [fetchLayout]);

  const saveLayout = useCallback(
    async (layout) => {
      if (!shopOrigin || !layout || layout.length === 0) return;

      try {
        await axios.post(`/api/save-layout?shop=${shopOrigin}`, {
          layout: layout,
        });
      } catch (error) {
        console.error("Error saving layout:", error);
        setBanner({
          show: true,
          message: "Could not save dashboard layout.",
          status: "critical",
        });
      }
    },
    [shopOrigin]
  );

  useEffect(() => {
    if (layoutLoading) {
      return;
    }

    const handler = setTimeout(() => {
      saveLayout(gridLayout);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [gridLayout, saveLayout, layoutLoading]);

  const [draggingHeight, setDraggingHeight] = useState({});
  const [chartsChartType, setChartsChartType] = useState("line");
  const [selectedChartView, setSelectedChartView] = useState("sessions");

  const fetchAssistantInstruction = useCallback(async () => {
    if (!shopOrigin) return;
    try {
      const response = await axios.get(
        `/api/get-shop-assistant-instruction/?shop=${shopOrigin}`
      );
      setAssistantInstruction(
        response.data.instruction ||
          "Default instruction: Write engaging blog posts."
      );
    } catch (error) {
      console.error("Error fetching assistant instruction:", error);
      setAssistantInstruction(
        "Default instruction: Write engaging blog posts."
      );
    }
  }, [shopOrigin]);

  useEffect(() => {
    if (!shopOrigin) return;
    fetchAssistantInstruction();
  }, [shopOrigin, fetchAssistantInstruction]);

  // Default grid layout - no plan restrictions
  const getDefaultGridLayout = () => {
    return [
      { id: "blogOverview", colSpan: 6 },
      { id: "customerSupport", colSpan: 6 },
      { id: "performance", colSpan: 4 },
      { id: "optimization", colSpan: 4 },
      { id: "strategic", colSpan: 4 },
      { id: "sessions", colSpan: 6 },
      { id: "engagement", colSpan: 6 },
      { id: "products", colSpan: 4 },
      { id: "charts", colSpan: 8 },
    ];
  };

  const calculateRowLayout = (items) => {
    const GRID_COLUMNS = 12;
    const rowLayouts = [];
    let currentRow = [];
    let currentRowSpan = 0;

    items.forEach((item, index) => {
      const nextSpan = currentRowSpan + item.colSpan;

      if (nextSpan <= GRID_COLUMNS) {
        currentRow.push({ ...item, adjustedColSpan: item.colSpan });
        currentRowSpan = nextSpan;
      } else {
        const remainingSpace = GRID_COLUMNS - currentRowSpan;

        if (remainingSpace >= 4 && item.colSpan > remainingSpace) {
          currentRow.push({ ...item, adjustedColSpan: remainingSpace });
          rowLayouts.push(currentRow);

          currentRow = [];
          currentRowSpan = 0;
        } else {
          if (currentRow.length > 0 && remainingSpace > 0) {
            const lastItem = currentRow[currentRow.length - 1];
            lastItem.adjustedColSpan =
              (lastItem.adjustedColSpan || lastItem.colSpan) + remainingSpace;
          }

          rowLayouts.push(currentRow);
          currentRow = [{ ...item, adjustedColSpan: item.colSpan }];
          currentRowSpan = item.colSpan;
        }
      }

      if (index === items.length - 1 && currentRow.length > 0) {
        const remainingSpace = GRID_COLUMNS - currentRowSpan;
        if (remainingSpace > 0 && currentRow.length > 0) {
          const lastItem = currentRow[currentRow.length - 1];
          lastItem.adjustedColSpan =
            (lastItem.adjustedColSpan || lastItem.colSpan) + remainingSpace;
        }
        rowLayouts.push(currentRow);
      }
    });

    return rowLayouts.flat();
  };

  const customCollisionDetection = (args) => {
    const closestCornersCollisions = closestCorners(args);

    if (closestCornersCollisions.length > 0) {
      return closestCornersCollisions;
    }

    return closestCenter(args);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  function handleDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;

    setGridLayout((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = [...items];
        newItems[oldIndex].colSpan = newItems[newIndex].colSpan;
        const reordered = arrayMove(newItems, oldIndex, newIndex);
        const preservedSpans = reordered.map((item) => ({
          ...item,
          colSpan: item.colSpan || item.adjustedColSpan || 4,
        }));
        return calculateRowLayout(preservedSpans);
      }
      return items;
    });

    setDraggingHeight((prev) => {
      const newPrev = { ...prev };
      delete newPrev[active.id];
      return newPrev;
    });
  }

  const createHeaderWithTooltip = (label, tooltipContent) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "4px",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "13px" }}>{label}</span>
        <ToolTip content={tooltipContent} dismissOnMouseOut>
          <Icon source={InfoIcon} tone="base" />
        </ToolTip>
      </div>
    );
  };

  const createHeaderWithTooltip_session = (label, tooltipContent) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "4px",
          marginRight: "-6px",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "13px" }}>{label}</span>
        <ToolTip content={tooltipContent} dismissOnMouseOut>
          <Icon source={InfoIcon} tone="base" />
        </ToolTip>
      </div>
    );
  };

  const createHeaderWithTooltip_product = (label, tooltipContent) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "4px",
          marginRight: "-6px",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "13px" }}>{label}</span>
        <ToolTip content={tooltipContent} dismissOnMouseOut>
          <Icon source={InfoIcon} tone="base" />
        </ToolTip>
      </div>
    );
  };

  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [scheduledTime, setScheduledTime] = useState("");
  const [{ month, year }, setDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });
  const [selectedDates, setSelectedDates] = useState({
    start: new Date(),
    end: new Date(),
  });
  const [formattedScheduledTime, setFormattedScheduledTime] = useState("");
  const [scheduledDateTime, setScheduledDateTime] = useState(null);
  const [shopTimezone, setShopTimezone] = useState("UTC");
  const [isPublished, setIsPublished] = useState();

  useEffect(() => {
    if (banner.show) {
      const timeoutId = setTimeout(() => {
        setBanner({ show: false, message: "", status: "success" });
      }, 100000);

      return () => clearTimeout(timeoutId);
    }
  }, [banner.show]);

  const fetchInsights = useCallback(async () => {
    if (!shopOrigin) return;

    setInsightsLoading(true);
    try {
      const response = await axios.get(
        `/analytics/generate-insights/?shop=${shopOrigin}&days=3`
      );

      setInsights({
        performance_insights: response.data.performance_insights || [],
        content_optimization: response.data.content_optimization || [],
        strategic_actions: response.data.strategic_actions || [],
      });
    } catch (error) {
      console.error("Error fetching insights:", error);
      setInsights({
        performance_insights: [],
        content_optimization: [],
        strategic_actions: [],
      });
    } finally {
      setInsightsLoading(false);
    }
  }, [shopOrigin]);

  useEffect(() => {
    if (!shopOrigin) return;
    // Defer analytics calls — let the critical path (articles, counts, timezone)
    // resolve first before these heavier queries start.
    const t = setTimeout(() => fetchInsights(), 400);
    return () => clearTimeout(t);
  }, [shopOrigin, fetchInsights]);

  const fetchTopPostsBySessions = useCallback(
    async (since, until, limit) => {
      setSessionLoading(true);
      try {
        const response = await axios.get(
          `/analytics/top-posts/?shop=${shopOrigin}&since=${formatDateToYearMonthDayDateString(
            since
          )}&until=${formatDateToYearMonthDayDateString(until)}&limit=${limit}`
        );

        const rows = response.data.map((post) => [
          <Link
            className="Polaris-Text--root Polaris-Text--bodySm"
            to={`/edit-blogs?shop=${encodeURIComponent(
              shopOrigin
            )}&article_id=${encodeURIComponent(post.article_id)}`}
            style={{
              color: "#303030",
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            {post.title || "Untitled"}
          </Link>,
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              gap: "4px",
            }}
          >
            <ToolTip content="Total number of views for this article">
              <Text as="span" variant="bodyMd">
                {post.views.toString()}
              </Text>
            </ToolTip>
          </div>,
        ]);
        setTopPostsBySessions(rows);
      } catch (error) {
        console.error("Error fetching top posts by sessions:", error);
        setTopPostsBySessions([]);
      } finally {
        setSessionLoading(false);
      }
    },
    [shopOrigin]
  );

  const fetchTopPostsByEngagement = useCallback(
    async (since, until, limit) => {
      setEngagementLoading(true);
      try {
        const response = await axios.get(
          `/analytics/engagement/?shop=${shopOrigin}&since=${formatDateToYearMonthDayDateString(
            since
          )}&until=${formatDateToYearMonthDayDateString(until)}&limit=${limit}`
        );
        const postsWithEngagement = response.data.per_article
          .map((post) => {
            const engagementPercent = Math.min(
              Math.round((post.avg_seconds / 300) * 100),
              100
            );
            return {
              title: post.title || post.handle || "Untitled",
              engagement: engagementPercent,
              avg_seconds: post.avg_seconds,
              article_id: post.article_id,
            };
          })
          .sort((a, b) => b.engagement - a.engagement);

        const rows = postsWithEngagement.map((post) => [
          <Link
            className="Polaris-Text--root Polaris-Text--bodySm"
            to={`/edit-blogs?shop=${encodeURIComponent(
              shopOrigin
            )}&article_id=${encodeURIComponent(post.article_id || "")}`}
            style={{
              color: "#303030",
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            {post.title || "Untitled"}
          </Link>,
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              gap: "4px",
            }}
          >
            <ToolTip content="How much you have interacted with the article">
              <Text as="span" variant="bodyMd">
                {post.engagement}%
              </Text>
            </ToolTip>
          </div>,
        ]);
        setTopPostsByEngagement(rows);
      } catch (error) {
        console.error("Error fetching top posts by engagement:", error);
        setTopPostsByEngagement([]);
      } finally {
        setEngagementLoading(false);
      }
    },
    [shopOrigin]
  );

  const fetchTopClickedProducts = useCallback(
    async (since, until) => {
      setProductsLoading(true);
      try {
        const response = await axios.get(
          `/analytics/top-clicked-products/?shop=${shopOrigin}&since=${formatDateToYearMonthDayDateString(
            since
          )}&until=${formatDateToYearMonthDayDateString(until)}`
        );
        const rows = response.data.map((product) => [
          product.title || "Untitled Product",
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              gap: "4px",
            }}
          >
            <ToolTip content="Total number of clicks on this product">
              <Text as="span" variant="bodyMd">
                {product.clicks.toString()}
              </Text>
            </ToolTip>
          </div>,
        ]);
        setTopClickedProducts(rows);
      } catch (error) {
        console.error("Error fetching top clicked products:", error);
        setTopClickedProducts([]);
      } finally {
        setProductsLoading(false);
      }
    },
    [shopOrigin]
  );

  const fetchChartData = useCallback(
    async (since, until, limit) => {
      setChartsLoading(true);
      try {
        const response = await axios.get(
          `/analytics/charts-by-session/?shop=${shopOrigin}&since=${formatDateToYearMonthDayDateString(
            since
          )}&until=${formatDateToYearMonthDayDateString(until)}&limit=${limit}`
        );
        const data = Array.isArray(response.data) ? response.data : [];
        const limitValue = limit === "All" ? data.length : parseInt(limit);
        setChartData(data.slice(0, limitValue));
      } catch (error) {
        console.error("Error fetching chart data:", error);
        setChartData([]);
      } finally {
        setChartsLoading(false);
      }
    },
    [shopOrigin]
  );

  useEffect(() => {
    if (!shopOrigin) return;
    const t = setTimeout(
      () => fetchTopClickedProducts(dateRangeProducts.since, dateRangeProducts.until),
      400
    );
    return () => clearTimeout(t);
  }, [shopOrigin, dateRangeProducts, fetchTopClickedProducts]);

  useEffect(() => {
    if (!shopOrigin) return;
    const t = setTimeout(
      () =>
        fetchTopPostsBySessions(
          dateRangeSessions.since,
          dateRangeSessions.until,
          topPostsSessionsCount
        ),
      400
    );
    return () => clearTimeout(t);
  }, [
    shopOrigin,
    dateRangeSessions,
    fetchTopPostsBySessions,
    topPostsSessionsCount,
  ]);

  useEffect(() => {
    if (!shopOrigin) return;
    const t = setTimeout(
      () =>
        fetchTopPostsByEngagement(
          dateRangeEngagement.since,
          dateRangeEngagement.until,
          topPostsEngagementsCount
        ),
      400
    );
    return () => clearTimeout(t);
  }, [
    shopOrigin,
    dateRangeEngagement,
    fetchTopPostsByEngagement,
    topPostsEngagementsCount,
  ]);

  useEffect(() => {
    if (!shopOrigin) return;
    const t = setTimeout(
      () =>
        fetchChartData(
          dateRangeCharts.since,
          dateRangeCharts.until,
          topPostsSessionsCount
        ),
      400
    );
    return () => clearTimeout(t);
  }, [
    shopOrigin,
    dateRangeCharts,
    fetchChartData,
    topPostsSessionsCount,
  ]);

  // useEffect(() => {
  //   if (!shopOrigin) return;
  //   fetchChartData(
  //     dateRangeCharts.since,
  //     dateRangeCharts.until,
  //     topPostsEngagementsCount
  //   );
  // }, [
  //   shopOrigin,
  //   dateRangeCharts,
  //   fetchChartData,
  //   topPostsEngagementsCount,
  // ]);

  // useEffect(() => {
  //   if (!shopOrigin) return;
  //   fetchChartData(
  //     dateRangeCharts.since,
  //     dateRangeCharts.until,
  //     topPostsProductsCount
  //   );
  // }, [
  //   shopOrigin,
  //   dateRangeCharts,
  //   fetchChartData,
  //   topPostsProductsCount,
  // ]);

  const handleResetLayout = async () => {
    if (!shopOrigin) return;
    try {
      await axios.post(`/api/reset-layout?shop=${shopOrigin}`);

      const defaultLayout = getDefaultGridLayout();
      const calculatedLayout = calculateRowLayout(defaultLayout);
      setGridLayout(calculatedLayout);

      setBanner({
        show: true,
        message: "Dashboard layout has been reset.",
        status: "success",
      });
      setEditMode(false);
    } catch (error) {
      console.error("Error resetting layout:", error);
      setBanner({
        show: true,
        message: "Failed to reset layout.",
        status: "critical",
      });
    }
  };

  const handleSetVisibilityDate = async () => {
    try {
      const [hours, minutes] = scheduledTime.split(":");
      const updatedDate = new Date(selectedDates.start);

      if (isNaN(updatedDate.getTime())) {
        console.error("Invalid date selected");
        setBanner({
          show: true,
          message: "Invalid date selected",
          status: "critical",
        });
        return;
      }

      updatedDate.setHours(parseInt(hours, 10), parseInt(minutes, 10));

      const momentDate = moment(updatedDate);
      const utcDateTime = momentDate.utc().format("YYYY-MM-DDTHH:mm:ss");
      const localFormatted = momentDate.format("ddd MMM DD YYYY HH:mm");
      const isFuture = isDateInFuture(updatedDate, "local");

      const response = await axios.post(
        `/api/update-article-schedule/?shop=${shopOrigin}`,
        {
          shop: shopOrigin,
          article_id: selectedArticle?.article_id,
          published_at: utcDateTime,
          published: !isFuture,
        }
      );

      if (response.data.status === "success") {
        setBanner({
          show: true,
          message: `Article scheduled for ${localFormatted}`,
          status: "success",
        });

        setScheduledDateTime(utcDateTime);
        setIsPublished(!isFuture);
        setFormattedScheduledTime(localFormatted);
        setIsDateModalOpen(false);
        fetchArticles();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error("Error scheduling article:", error);
      setBanner({
        show: true,
        message: "Failed to schedule article",
        status: "critical",
      });
    }
  };

  const isDateInFuture = (date, timezone) => {
    if (!date) return false;
    if (timezone === "local") {
      return moment(date).isAfter(moment());
    }
    const now = moment.tz(timezone || "UTC");
    const targetDate = moment.tz(date, timezone || "UTC");
    return targetDate.isAfter(now);
  };

  const fetchBlogCounts = useCallback(async () => {
    try {
      const response = await axios.get(
        `/api/get-articles-count?shop=${shopOrigin}`
      );
      const { total_articles, published_articles, unpublished_articles } =
        response.data;
      setBlogCounts({
        total: total_articles,
        published: published_articles,
        unpublished: unpublished_articles,
      });
    } catch (error) {
      console.error("Error fetching blog counts:", error);
    }
  }, [shopOrigin]);

  const fetchArticles = useCallback(async () => {
    try {
      const response = await axios.get(`/api/get-articles/?shop=${shopOrigin}`);

      const raw = response.data.articles;
      const totalCount = raw.length;

      const publishedCount = raw.filter(
        (a) => a.published && !a.deleted
      ).length;
      const unpublishedCount = raw.filter(
        (a) => !a.published && !a.deleted
      ).length;
      setBlogTableCounts((prevCounts) => ({
        ...prevCounts,
        published: publishedCount,
        unpublished: unpublishedCount,
        total: totalCount,
      }));

      const filtered = raw.filter((a) => {
        if (selectedTab === 1) return a.published && !a.deleted;
        if (selectedTab === 2) return !a.published && !a.deleted;
        return !a.deleted;
      });

      const formatted = filtered.map((article, index) => ({
        id: article.article_id,
        article_id: article.article_id,
        title: article.article_title,
        author: article.author || "Anonymous",
        status: article.published
          ? article.published_at &&
            isDateInFuture(
              moment.tz(article.published_at, shopTimezone),
              shopTimezone
            )
            ? "Scheduled"
            : "Published"
          : "Unpublished",
        blog: article.blog_title || "News",
        updated: article.updated_at || article.created_at,
        published_at: article.published_at,
        raw: article,
      }));
      setArticles(formatted);
    } catch (error) {
      console.error("Error loading articles: ", error);
      setArticles([]);
    }
  }, [shopOrigin, selectedTab]);

  const handleOptimizeArticle = async (item) => {
    let progressInterval;

    try {
      setIsProcessing(true);
      setProcessingProgress(0);
      setProcessingMessage("Optimizing your article...");
      setProcessingType("optimize");
      setProcessingArticleId(item.article_id);

      progressInterval = simulateProgress(98, 30000);

      const originalHtml = item.raw.article_body || item.raw.content || "";
      const originalTitle =
        item.raw.article_title || item.raw.title || item.title;

      const optimizeResponse = await axios.post(
        `/api/optimize-article/${encodeURIComponent(shopOrigin || "")}/`,
        {
          article_id: item.article_id,
          title: originalTitle,
          content: originalHtml,
          language: item.raw.language || "English",
          tone: item.raw.tone || undefined,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      clearInterval(progressInterval);
      setProcessingProgress(100);

      if (
        optimizeResponse.data.status === "success" &&
        optimizeResponse.data.article
      ) {
        const optimizedArticle = optimizeResponse.data.article;

        // ── NEW BEHAVIOUR ──────────────────────────────────────────────────────
        // Do NOT auto-save. Store the result and open the confirmation modal so
        // the user can review the optimised content before anything is changed
        // in Shopify.
        setOptimisedArticlePreview({
          article_title: optimizedArticle.article_title,
          article_body: optimizedArticle.article_body,
          article_id: item.article_id,
          status: item.status,
          blog_id: item.raw.blog_id,
        });
        setOptimisedArticleModalOpen(true);
        // ──────────────────────────────────────────────────────────────────────
      } else {
        throw new Error(
          optimizeResponse.data.message ||
            "Optimization failed to return valid content."
        );
      }
    } catch (err) {
      if (progressInterval) clearInterval(progressInterval);

      console.error("Optimization process failed:", err.message);

      showToast(
        err.message || "We couldn't optimize this article right now. Please try again.",
        true
      );

      setBanner({
        show: true,
        message:
          err.message || "We couldn't optimize this article right now. Please try again.",
        status: "critical",
      });
    } finally {
      setIsProcessing(false);
      setProcessingArticleId(null);
      setProcessingType(null);
      setProcessingProgress(0);
    }
  };

  /**
   * Called when the user clicks "Save as Draft" inside the
   * OptimisedArticleModal. Saves a new draft copy to Shopify (keeping the
   * original article untouched) and opens it for editing.
   */
  const handleConfirmOptimise = async () => {
    if (!optimisedArticlePreview) return;

    setIsConfirmingOptimise(true);

    try {
      const rawTitle = optimisedArticlePreview.article_title || "";
      const lower = rawTitle.trim().toLowerCase();
      const suffixes = [" - optimized", " (optimized)"];
      const hasSuffix = suffixes.some((s) => lower.endsWith(s));
      const titleWithOptimized = hasSuffix ? rawTitle : `${rawTitle} - optimized`;

      const payload = {
        article_title: titleWithOptimized,
        article_content: optimisedArticlePreview.article_body,
        status: "draft", // always create a safe draft copy
      };

      if (optimisedArticlePreview.blog_id) {
        payload.blog_id = optimisedArticlePreview.blog_id;
      }

      const publishResponse = await axios.post(
        `/publish-article/${shopOrigin}/`,
        payload
      );

      if (publishResponse.data.status === "success") {
        setOptimisedArticleModalOpen(false);
        setOptimisedArticlePreview(null);
        showToast("Draft saved. Your original article is unchanged.");

        const newArticleId = publishResponse.data.article_id;

        if (newArticleId) {
          navigate(
            `/edit-blogs?shop=${encodeURIComponent(
              shopOrigin
            )}&article_id=${encodeURIComponent(newArticleId)}`
          );
        }
      } else {
        throw new Error(
          publishResponse.data.message ||
            "Failed to save the optimised article to Shopify."
        );
      }
    } catch (err) {
      console.error("Confirm optimise failed:", err.message);

      showToast(
        err.message || "We couldn't save this draft right now. Please try again.",
        true
      );

      setBanner({
        show: true,
        message: err.message || "We couldn't save this draft right now. Please try again.",
        status: "critical",
      });
    } finally {
      setIsConfirmingOptimise(false);
    }
  };

  const handleGenerateSimilar = async (item) => {
    try {
      setIsProcessing(true);
      setProcessingProgress(0);
      setProcessingMessage("Generating similar article...");
      setProcessingType("generate");
      setProcessingArticleId(item.article_id);

      const progressInterval = simulateProgress(98, 30000);

      const response = await axios.post(
        `/api/generate-similar-article/?shop=${shopOrigin}`,
        {
          prompt: assistantInstruction,
          base_title: item.title,
          language: item.raw.language || "English",
          tone: item.raw.tone || "professional",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      clearInterval(progressInterval);
      setProcessingProgress(100);

      if (response.data.status === "success" && response.data.article) {
        const newArticle = {
          ...response.data.article,
          published: false,
          createdat: new Date().toISOString(),
          updatedat: new Date().toISOString(),
        };

        setGeneratedArticle(newArticle);

        showToast("Similar article generated successfully! ✨");

        setModalOpen(true);
      } else {
        throw new Error(response.data.message || "Generation failed");
      }
    } catch (error) {
      console.error("Error generating similar article:", error);

      showToast("Failed to generate similar article", true);

      setBanner({
        show: true,
        message: "Failed to generate similar article",
        status: "critical",
      });
    } finally {
      setTimeout(() => {
        setIsProcessing(false);
        setProcessingArticleId(null);
        setProcessingType(null);
        setProcessingProgress(0);
      }, 500);
    }
  };

  useEffect(() => {
    if (!shopOrigin) return;

    // ── LCP optimisation ────────────────────────────────────────────────────
    // Fire all three requests in parallel but handle each independently.
    // The Page shell is already visible; each section updates the moment its
    // own data arrives instead of waiting for the slowest request.
    // ────────────────────────────────────────────────────────────────────────

    // 1. Timezone + owner name  (lightweight — resolves first)
    axios
      .get(`/api/get-shop-timezone/?shop=${shopOrigin}`)
      .then((res) => {
        if (res.data.status === "success") {
          let tz = res.data.timezone || "UTC";
          if (/^[+-]\d{4}$/.test(tz)) tz = "UTC";
          setShopTimezone(tz);
          setShopOwnerName(res.data.shop_owner_name || "User");
        } else {
          setShopTimezone("UTC");
        }
      })
      .catch(() => {
        setShopTimezone("UTC");
        setShopOwnerName("User");
      });

    // 2. Blog counts  (fast aggregate query)
    axios
      .get(`/api/get-articles-count?shop=${shopOrigin}`)
      .then((res) => {
        setBlogCounts({
          total: res.data.total_articles || 0,
          published: res.data.published_articles || 0,
          unpublished: res.data.unpublished_articles || 0,
        });
      })
      .catch(() => {
        setBlogCounts({ total: 0, published: 0, unpublished: 0 });
      });

    // 3. Articles list  (heaviest — Page is already visible while this loads)
    setLoading(true);
    axios
      .get(`/api/get-articles/?shop=${shopOrigin}`)
      .then((res) => {
        const raw = res.data.articles || [];

        // Derive counts from the raw list as well (keeps table counts in sync)
        const publishedCount = raw.filter((a) => a.published && !a.deleted).length;
        const unpublishedCount = raw.filter((a) => !a.published && !a.deleted).length;
        setBlogTableCounts({
          total: raw.length,
          published: publishedCount,
          unpublished: unpublishedCount,
        });

        const formatted = raw
          .filter((a) => !a.deleted)
          .map((article) => {
            let status = "Unpublished";
            if (article.published) {
              if (article.published_at) {
                let targetDate, now;
                if (/^[+-]\d{4}$/.test(shopTimezone)) {
                  targetDate = moment(article.published_at).utcOffset(shopTimezone);
                  now = moment().utcOffset(shopTimezone);
                } else {
                  targetDate = moment.tz(article.published_at, shopTimezone || "UTC");
                  now = moment.tz(shopTimezone || "UTC");
                }
                status = targetDate.isAfter(now) ? "Scheduled" : "Published";
              } else {
                status = "Published";
              }
            }
            return {
              id: article.article_id,
              article_id: article.article_id,
              title: article.article_title,
              author: article.author || "Anonymous",
              status,
              blog: article.blog_title || "News",
              updated: article.updated_at || article.created_at,
              published_at: article.published_at,
              raw: article,
            };
          });

        setArticles(formatted);
      })
      .catch((error) => {
        console.error("Error loading articles:", error);
        setArticles([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [shopOrigin]);

  useEffect(() => {
    if (articles.length === 0) return;

    const now = moment();
    const TARGET_ARTICLES_PER_WEEK = 5;

    const thisWeekArticles = articles.filter((article) => {
      const created = moment(article.createdat || article.updated);
      return created.isValid() && created.isSame(now, "week");
    });

    const published = thisWeekArticles.filter(
      (article) => article.status !== "Unpublished"
    ).length;

    setPublishedThisWeek(published);
    setTotalThisWeek(TARGET_ARTICLES_PER_WEEK);
  }, [articles]);

  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const fraction =
    totalCount > 0
      ? selectedStatus === "published"
        ? blogCounts.published / totalCount
        : blogCounts.unpublished / totalCount
      : 0;
  const dashoffset = circumference * (1 - fraction);

  const navigateToneSetup = () => {
    navigate(`/tone-content?shop=${encodeURIComponent(shopOrigin || "")}`);
  };

  const navigateFreeInstall = () => {
    window.open("https://apps.shopify.com/analyticshero", "_blank");
  };
  const rowMarkup = articles.map((item, index) => (
    <IndexTable.Row id={item.id} key={item.id} position={index}>
      <IndexTable.Cell>
        <div>
          <Text fontWeight="medium" as="span">
            {item.title}
          </Text>
          <div style={{ color: "#6d7175", fontSize: "13px" }}>
            {item.author}
          </div>
        </div>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span">
          {item.status === "Published" ? (
            <Badge tone="success">Published</Badge>
          ) : (
            <Badge>Draft</Badge>
          )}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text fontWeight="medium" as="span" tone="subdued">
          {item.blog}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {item.updated
          ? format(parseISO(item.updated), "dd/MM/yyyy, HH:mm:ss")
          : "-"}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            flexDirection: "row",
            whiteSpace: "nowrap",
          }}
        >
          <div style={{ minWidth: "120px", position: "relative" }}>
            <Button
              variant="primary"
              disabled={isProcessing}
              onClick={() => handleOptimizeArticle(item)}
              fullWidth
            >
              {processingArticleId === item.article_id &&
              isProcessing &&
              processingType === "optimize" ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                  }}
                >
                  <div>
                    <p
                      style={{ fontSize: "12px" }}
                      className="processing-submessage"
                    >
                      {processingProgress}%
                    </p>
                  </div>
                  <div>
                    <Spinner
                      accessibilityLabel="Loading spinner"
                      size="small"
                    />
                  </div>
                </div>
              ) : (
                "Optimize"
              )}
            </Button>
          </div>

          <div style={{ minWidth: "150px", position: "relative" }}>
            <Button
              disabled={isProcessing}
              onClick={() => handleGenerateSimilar(item)}
              fullWidth
            >
              {processingArticleId === item.article_id &&
              isProcessing &&
              processingType === "generate" ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                  }}
                >
                  <div>
                    <p
                      style={{ fontSize: "12px" }}
                      className="processing-submessage"
                    >
                      {processingProgress}%
                    </p>
                  </div>
                  <div>
                    <Spinner
                      accessibilityLabel="Loading spinner"
                      size="small"
                    />
                  </div>
                </div>
              ) : (
                "Generate Similar"
              )}
            </Button>
          </div>
          <Popover
            active={activePopoverId === item.id}
            activator={
              <div
                style={{ cursor: "pointer" }}
                onClick={() =>
                  setActivePopoverId((prev) =>
                    prev === item.id ? null : item.id
                  )
                }
              >
                <Icon source={MenuHorizontalIcon} tone="base" />
              </div>
            }
            onClose={() => setActivePopoverId(null)}
          >
            <ActionList
              items={[
                {
                  content: "View",
                  onAction: () => {
                    setSelectedArticle(item.raw);
                    setModalOpen(true);
                    setActivePopoverId(null);
                  },
                },
                {
                  content: "Edit",
                  onAction: () => {
                    navigate(
                      `/edit-blogs?shop=${encodeURIComponent(
                        shopOrigin
                      )}&article_id=${encodeURIComponent(item.article_id)}`
                    );
                    setActivePopoverId(null);
                  },
                },
                {
                  content: "Schedule",
                  onAction: () => {
                    if (item.raw.published_at) {
                      const scheduledDate = moment
                        .tz(item.raw.published_at, shopTimezone)
                        .toDate();

                      setSelectedDates({
                        start: scheduledDate,
                        end: scheduledDate,
                      });

                      setScheduledTime(
                        moment(scheduledDate).format("HH:mm")
                      );

                      setDate({
                        month: scheduledDate.getMonth(),
                        year: scheduledDate.getFullYear(),
                      });
                    } else {
                      const today = new Date();
                      setSelectedDates({ start: today, end: today });
                      setScheduledTime("");
                      setDate({
                        month: today.getMonth(),
                        year: today.getFullYear(),
                      });
                    }

                    setSelectedArticle(item.raw);
                    setIsDateModalOpen(true);
                    setActivePopoverId(null);
                  },
                },
                {
                  content:
                    item.status === "Published" ? "Unpublish" : "Publish",
                  onAction: () => {
                    setSelectedArticle(item.raw);
                    setStatusModalOpen(true);
                    setActivePopoverId(null);
                  },
                },
                {
                  content: "Delete",
                  destructive: true,
                  onAction: () => {
                    setSelectedArticle(item.raw);
                    setDeleteModalOpen(true);
                    setActivePopoverId(null);
                  },
                },
              ]}
            />
          </Popover>
        </div>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));


  const customStyles = `

    .Polaris-DatePicker__Header{
      transform: translateY(-10px);
    }

    /* âœ… Overlay Loader Styles */
    .processing-overlay {
      position: fixed;
      inset: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
      backdrop-filter: blur(4px);
      overflow: hidden;
    }

    .processing-content {
      background: #fff;
      padding: 32px 48px;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 20px;
      max-width: 90vw;
      width: 400px;
      text-align: center;
      box-sizing: border-box;
    }

    .Polaris-Spinner--sizeSmall svg {
      height: 1.1rem;
      width: 1.1em;
    }

    .processing-spinner {
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    .processing-message {
      font-size: 18px;
      font-weight: 500;
      color: #202223;
    }

    .processing-submessage {
      font-size: 14px;
      color: #6d7175;
    }


    /* âœ… Responsive for small screens */
    @media (max-width: 768px) {
      .processing-content {
        padding: 24px;
        width: 100%;
        max-width: 95vw;
        gap: 16px;
      }
      .processing-message {
        font-size: 16px;
      }
      .processing-submessage {
        font-size: 13px;
      }
    }
    .dashboard-header {
      text-align: center;
      margin-bottom: 24px;
    }

    .dashboard-header h2 {
      margin-bottom: 8px;
    }
    
    .edit_dashboard_div{
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }
      
    @media(max-width: 425px){
      .edit_dashboard_div{
        display: flex;
        justify-content: center;
        gap: 10px;
      }
    }

    .dashboard-stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 16px;
      margin: 24px 0;
    }

    .dashboard-stat-card {
      height: auto !important;
      min-height: 140px;
    }
    .dashboard-stat-card.card3{
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
    }

    .dashboard-stat-content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;
      padding: 16px 0;
    }
    .dashboard-stat-content.card3 {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      text-align: left;
      padding: 16px 0;
    }
    .dashboard-stat-content.card3.Polaris-Button {
      width: 100%;
      text-align: center;
    }

    .dashboard-stat-number {
      font-size: 2.5rem;
      margin: 12px 0;
    }

    .blog-overview-wrapper {
      display: flex;
      align-items: center;
      gap: 24px;
      width: 100%;
      margin-top: 20px;
      margin-bottom: 20px;
    }

    .count-display {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin-top: -34px;
    }

    .total-count-circle {
      position: relative;
      width: 60px;
      height: 60px;
      margin-top: -10px;
    }

    .progress-ring {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    .progress-ring__background {
      fill: none;
      stroke: #d6d6d6;
      stroke-width: 7;
    }

    .progress-ring__progress {
      fill: none;
      stroke: #000000;
      stroke-width: 7;
      stroke-dasharray: ${circumference};
      stroke-dashoffset: ${dashoffset};
      transform: rotate(-90deg);
      transform-origin: 50% 50%;
      transition: stroke-dashoffset 0.5s ease;
    }

    .total-count-number {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 30px;
      font-weight: 500;
      color: #202223;
      line-height: 1;
    }

    .publish_unpublish {
      display: flex;
      flex-direction: column;
      gap: 12px;
      width: 100%;
    }

    .status-option {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .status-count {
      font-size: 14px;
      color: black;
      font-weight: 500;
    }

    .dashboard-section-header {
      display: flex;
      justify-content: end;
      align-items: center;
      margin-top: 15px;
      flex-wrap: wrap;
      gap: 16px;
    }
    .dashboard-section-header-recent{
      display: flex;
      justify-content: end;
      align-items: center;
      padding: 15px;
      flex-wrap: wrap;
      gap: 16px;
    }
    .dashboard-section-title {
      flex: 1;
      min-width: 150px;
    }

    .dashboard-create-button {
      flex-shrink: 0;
    }

    .dashboard-table-container {
      margin-top: 15px;
      margin-bottom: 24px;
    }

    .dashboard-see-all-container {
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 24px 0;
    }

    td:first-child{
      width: 40%;
    }
    td:nth-child(3){
      width: 15%;
    }
    .dashboard_article_week{
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px
    }

    .dashboard-grid-container {
      position: relative;
      display: grid;
      grid-template-columns: repeat(12, 1fr);
      gap: 16px;
      margin: 24px 0;
    }

    .dashboard-grid-container > * {
      min-width: 0;
      width: 100%;
      transition: grid-column 200ms ease;
    }

    .sortable-card {
      transition: transform 200ms ease, opacity 200ms ease, box-shadow 200ms ease;
      will-change: transform;
    }

    /* âœ… Remove transition for the actively dragged card */
    .sortable-card[style*="translate"] {
      transition: none !important;
    }
    .sortable-card[data-dragging="true"] {
      transition: none !important;
      cursor: grabbing !important;
      overflow: hidden;
    }

    /* âœ… Create visual placeholder for drop zone */
    .sortable-card[data-dragging="true"]::after {
      content: "";
      position: absolute;
      inset: 0;
      border: 2px dashed #005bd3;
      border-radius: 8px;
      pointer-events: none;
      opacity: 0.4;
    }
    
    /* Scope to this picker only */
    .dp-with-header .Polaris-DatePicker__Title {
      display: none;
    }

    .dp-header {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 8px 12px;
    }

    .dp-label {
      font-weight: 600;
    }
    /* Make all draggable cards consistent within groups */
    .dnd-grid .dashboard-stat-card,
    .dnd-item .Polaris-Card,
    .dnd-item .Polaris-LegacyCard {
      min-height: var(--card-height, 220px);
      height: var(--card-height, 220px);
      width: 100%;
      box-sizing: border-box;
    }

    // Update customStyles (replace the existing .dnd-item.is-edit rules)
    .dnd-item.is-edit .Polaris-Card,
    .dnd-item.is-edit .Polaris-LegacyCard,
    .dnd-item.is-edit .Polaris-ShadowBevel,
    .dnd-item.is-edit .Polaris-ShadowBevel {
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
      transform: translateY(-4px);
      transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
    }

    .dnd-item.is-edit:hover .Polaris-Card,
    .dnd-item.is-edit:hover .Polaris-LegacyCard,
    .dnd-item.is-edit:hover .Polaris-ShadowBevel,
    .dnd-item.is-edit:hover .Polaris-ShadowBevel {
      box-shadow: 0 6px 16px rgba(0,0,0,0.12);
      transform: translateY(-6px);
    }

    /* Ensure drag handle appears for all sortable cards */
    .dnd-item.is-edit .drag-handle {
      display: flex;
    }

    /* Sortable wrapper animations */
    .dnd-item {
      position: relative;
      will-change: transform;
    }


    /* Drag handle only in edit mode */
    .dnd-item .drag-handle {
      position: absolute;
      top: 8px;
      right: 8px;
      height: 28px;
      width: 28px;
      border-radius: 6px;
      display: none;
      align-items: center;
      justify-content: center;
      cursor: grab;
      z-index: 2;
      background: #f6f6f7;
      border: 1px solid #e3e3e3;
    }

    .dnd-item.is-edit .drag-handle {
      display: flex;
    }

    .sortable-card.is-edit {
      cursor: pointer;
    }


    /* Optional: elevated feedback while dragging */
    .dnd-item[style*="translate"] .Polaris-Card,
    .dnd-item[style*="translate"] .Polaris-LegacyCard {
      box-shadow: 0 8px 28px rgba(0,0,0,0.12);
    }

    /* Fixed height for insight cards */
    .insights-card {
      height: 252px;
      min-height: 252px;
      display: flex;
      flex-direction: column;
    }
    .insights-card .Polaris-Box{
      height: 252px;
      min-height: 252px;
      display: flex;
      flex-direction: column;
    }

    /* Fixed height for insight cards */
    .product_chart {
      height: 375px;
      min-height: 375px;
      display: flex;
      flex-direction: column;
    }
    .product_chart .Polaris-Box{
      height: 375px;
      min-height: 375px;
      display: flex;
      flex-direction: column;
    }
    .Polaris-IndexTable{
      padding-bottom: 10px;
    }
    .insights-card .Polaris-Card {
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .insights-card .Polaris-Card__Section {
      flex: 1;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
    }

    /* Make the BlockStack inside fill available space */
    .insights-card .Polaris-BlockStack {
      height: 100%;
    }

    /* Ensure smooth scrolling if content overflows */
    .insights-card .Polaris-Card__Section::-webkit-scrollbar {
      width: 6px;
    }

    .insights-card .Polaris-Card__Section::-webkit-scrollbar-thumb {
      background: #ddd;
      border-radius: 3px;
    }

    /* Make all draggable cards consistent within groups */
    .dashboard-grid-container .dashboard-stat-card,
    .sortable-card .Polaris-Card,
    .sortable-card .Polaris-LegacyCard {
      width: 100%;
      box-sizing: border-box;
      min-height: 200px;
    }

    .sortable-card {
      display: flex;
      flex-direction: column;
      height: auto;
      min-height: 200px;
      box-sizing: border-box;
    }
    
    /* âœ… When dragging, create placeholder space */
    .sortable-card[data-dragging="true"] {
      opacity: 0.4;
    }

    .sortable-card > * {
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    /* Specific card heights for better layout */
    .sortable-card .Polaris-LegacyCard {
      min-height: 200px;
    }

    .sortable-card .Polaris-Card {
      min-height: 250px;
    }

    // Update customStyles (replace the existing .sortable-card.is-edit rules)
    .sortable-card.is-edit .Polaris-Card,
    .sortable-card.is-edit .Polaris-LegacyCard,
    .sortable-card.is-edit .Polaris-ShadowBevel,
    .sortable-card.is-edit .Polaris-ShadowBevel {
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
      transform: translateY(-4px);
      transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
    }

    .sortable-card.is-edit:hover .Polaris-Card,
    .sortable-card.is-edit:hover .Polaris-LegacyCard,
    .sortable-card.is-edit:hover .Polaris-ShadowBevel,
    .sortable-card.is-edit:hover .Polaris-ShadowBevel {
      box-shadow: 0 6px 16px rgba(0,0,0,0.12);
      transform: translateY(-6px);
    }

    /* Optional: elevated feedback while dragging */
    .sortable-card[style*="translate"] .Polaris-Card,
    .sortable-card[style*="translate"] .Polaris-LegacyCard {
      box-shadow: 0 8px 28px rgba(0,0,0,0.12);
    }


    @media (max-width: 768px) {
      /* Grid container becomes single column flex */
      .dashboard-grid-container {
        display: flex !important;
        flex-direction: column !important;
        gap: 16px;
        margin: 24px 0;
        grid-template-columns: none !important;
      }

      /* All sortable cards flex column */
      .sortable-card {
        display: flex !important;
        flex-direction: column !important;
        width: 100% !important;
        grid-column: span 12 !important;
        min-height: auto !important;
      }

      /* Legacy cards and Card components */
      .dashboard-stat-card,
      .sortable-card .Polaris-LegacyCard,
      .sortable-card .Polaris-Card {
        display: flex !important;
        flex-direction: column !important;
        width: 100% !important;
        min-height: auto !important;
      }

      /* Card sections flex column */
      .Polaris-LegacyCard__Section,
      .Polaris-Card__Section {
        display: flex !important;
        flex-direction: column !important;
      }

      /* Stats grid single column */
      .dashboard-stats-grid {
        display: flex !important;
        flex-direction: column !important;
        gap: 12px;
        margin: 16px 0;
      }

      /* Section headers flex column */
      .dashboard-section-header,
      .dashboard-section-header-recent {
        display: flex !important;
        flex-direction: column !important;
        align-items: stretch;
        gap: 12px;
      }

      /* Blog overview wrapper flex column */
      .blog-overview-wrapper {
        display: flex !important;
        flex-direction: column !important;
        gap: 16px;
        width: 100%;
      }

      /* Publish/unpublish flex column */
      .publish_unpublish {
        display: flex !important;
        flex-direction: column !important;
        gap: 12px;
        width: 100%;
      }

      /* BlockStack and InlineStack to flex column */
      .Polaris-BlockStack {
        display: flex !important;
        flex-direction: column !important;
      }

      .Polaris-InlineStack {
        display: flex !important;
        flex-direction: column !important;
        align-items: stretch !important;
      }

      /* Progress and stats */
      .dashboard_article_week {
        display: flex !important;
        flex-direction: column !important;
        align-items: center;
        gap: 12px;
        text-align: center;
      }

      /* Buttons full width */
      .dashboard-create-button {
        width: 100%;
      }
      
      .dashboard-create-button .Polaris-Button {
        width: 100%;
      }
    }


    /* Tablet styles */
    @media (max-width: 1024px) {
      .dashboard-stats-grid {
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 12px;
      }

      .dashboard-section-header {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
      }

      .dashboard-section-title {
        text-align: center;
      }

      .dashboard-create-button {
        width: 100%;
      }
      
      .dashboard-create-button .Polaris-Button{
        width: 100%;
      }

      .dashboard-stat-number {
        font-size: 2.2rem;
      }
    }

    /* Mobile styles */
    @media (max-width: 768px) {
      .dashboard-header h2 {
        font-size: 1.8rem !important;
      }

      .dashboard-header p {
        font-size: 0.95rem;
        padding: 0 16px;
      }

      .dashboard-stats-grid {
        grid-template-columns: 1fr;
        gap: 12px;
        margin: 16px 0;
      }

      .dashboard-stat-card {
        min-height: 120px;
      }

      .dashboard-stat-number {
        font-size: 2rem;
        margin: 8px 0;
      }

      .dashboard-section-header {
        margin: 24px 0 12px 0;
      }

      .dashboard-create-button button {
        font-size: 0.9rem;
        padding: 12px 16px;
      }

      /* Table responsiveness */
      .Polaris-IndexTable__ScrollContainer {
        overflow-x: auto;
      }

      .Polaris-IndexTable-Table {
        min-width: 600px;
      }
    }

    @media(max-width: 1600px){
      .Polaris-IndexTable__TableCell {
        white-space: normal;
      }
    }

    @media(max-width: 2200px){
      .Polaris-IndexTable__TableCell {
        white-space: normal;
      }
    }
    


    /* Small mobile styles */
    @media (max-width: 480px) {
      .dashboard-header {
        margin-bottom: 16px;
      }

      .dashboard-header h2 {
        font-size: 1.5rem !important;
        line-height: 1.3;
      }

      .dashboard-header p {
        font-size: 0.9rem;
      }

      .dashboard-stats-grid {
        margin: 12px 0;
      }

      .dashboard-stat-number {
        font-size: 1.8rem;
      }

      .dashboard-section-header {
        margin: 20px 0 12px 0;
      }

      .dashboard-see-all-container {
        margin: 16px 0;
      }

      /* Further simplify table on very small screens */
      .Polaris-IndexTable-Cell:nth-child(2) {
        display: none;
      }

      .Polaris-IndexTable-Table {
        min-width: 400px;
      }
    }

    /* Legacy card specific styles */
    .Polaris-LegacyCard + .Polaris-LegacyCard {
      margin-top: 0px;
    }

    /* Tab */
    @media (max-width: 600px) {
      .Polaris-Tabs__TabContainer {
        // overflow-x: auto;
      }

      .Polaris-Tabs__Tab {
        white-space: nowrap;
        min-width: fit-content;
      }
    }

    @media(max-width: 1600px){
      .Polaris-IndexTable__TableCell {
        white-space: normal;
      }
    }

    @media(max-width: 2800px){
      .Polaris-IndexTable__TableCell {
        white-space: normal;
      }
    }
    .Polaris-IndexTable__TableCell {
      white-space: normal;
    }

    /* ========== Article Link Hover Effects ========== */
    .insights-card a[class*="Polaris-Text"] {
      color: #303030 !important;
      text-decoration: none !important;
      cursor: pointer !important;
      font-weight: 500;
      transition: color 0.2s ease, text-decoration 0.2s ease, transform 0.15s ease !important;
      display: inline-block;
    }

    .insights-card a[class*="Polaris-Text"]:hover {
      color: #005bd3 !important;
      text-decoration: underline !important;
      transform: translateY(-1px);
    }

    .insights-card a[class*="Polaris-Text"]:active {
      color: #004a99 !important;
      transform: translateY(0);
    }

    /* For mobile touch devices */
    @media (hover: none) {
      .insights-card a[class*="Polaris-Text"]:active {
        color: #005bd3 !important;
        text-decoration: underline !important;
      }
    }
  `;

  // Add this useEffect after your existing chart-related useEffects
  useEffect(() => {
    if (!shopOrigin) return;

    switch (selectedChartView) {
      case "sessions":
        fetchChartData(
          dateRangeCharts.since,
          dateRangeCharts.until,
          topPostsSessionsCount
        );
        break;
      case "engagement":
        fetchTopPostsByEngagement(
          dateRangeCharts.since,
          dateRangeCharts.until,
          topPostsEngagementsCount
        );
        break;
      case "products":
        fetchTopClickedProducts(dateRangeCharts.since, dateRangeCharts.until);
        break;
    }
  }, [
    shopOrigin,
    selectedChartView,
    dateRangeCharts,
    topPostsSessionsCount,
    topPostsEngagementsCount,
    fetchChartData,
    fetchTopPostsByEngagement,
    fetchTopClickedProducts,
  ]);

  
  const renderChartByType = (
    chartType,
    data,
    dataKey,
    xAxisKey,
    title,
    viewType = "sessions"
  ) => {
    const truncate = (str, n) => {
      return str?.length > n ? str.slice(0, n - 1) + "..." : str;
    };

    // Generate colors based on data length and view type
    const colors = generateColorPalette(data.length, viewType);
    
    // ✅ CRITICAL FIX: Normalize data structure based on view type
    const normalizedData = (data || []).map((item) => {
      const name = item.title || item.handle || item.name || "Untitled";
      let value = 0;

      // Extract the correct numeric value based on view type
      if (viewType === "engagement") {
        // Handle both raw engagement data and table-extracted data
        if (typeof item.engagement === 'number') {
          value = item.engagement;
        } else if (item.avg_seconds !== undefined) {
          // Convert avg_seconds to percentage (max 100%)
          value = Math.min(Math.round((item.avg_seconds / 300) * 100), 100);
        }
      } else if (viewType === "products") {
        // Products view uses 'clicks'
        value = Number(item.clicks) || 0;
      } else {
        // Sessions view uses 'views' (default)
        value = Number(item.views) || 0;
      }

      // Return normalized object with both xAxisKey and dataKey
      return {
        [xAxisKey]: name,
        [dataKey]: value,
        ...item, // Keep original data for debugging
      };
    });

    // ✅ Filter out any items with NaN or invalid values
    const validData = normalizedData.filter(item => 
      !isNaN(item[dataKey]) && item[dataKey] !== null && item[dataKey] !== undefined
    );

    // ✅ If no valid data, show empty state
    if (validData.length === 0) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '300px',
          color: '#6d7175'
        }}>
          <Text as="p" tone="subdued">
            No data available for this view
          </Text>
        </div>
      );
    }

    // ✅ Dynamic label based on view type
    const getValueLabel = (viewType) => {
      switch(viewType) {
        case 'engagement': return 'Engagement %';
        case 'products': return 'Clicks';
        case 'sessions':
        default: return 'Views';
      }
    };

    switch (chartType) {
      case "bar":
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={validData}
              margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey={xAxisKey}
                tick={{ fontSize: 12 }}
                interval={0}
                angle={-45}
                textAnchor="end"
                tickFormatter={(value) => truncate(value, 11)}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value, name, props) => [
                  value,
                  `${props.payload[xAxisKey]}: ${getValueLabel(viewType)}`,
                ]}
              />
              <Bar dataKey={dataKey} fill="#0088FE" animationDuration={800}>
                {validData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={colors[index % colors.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case "pie":
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={validData}
                dataKey={dataKey}
                nameKey={xAxisKey}
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={(entry) => truncate(entry[xAxisKey], 15)}
                animationDuration={800}
              >
                {validData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={colors[index % colors.length]}
                  />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value) => [value, getValueLabel(viewType)]}
              />
            </PieChart>
          </ResponsiveContainer>
        );

      case "line":
      default:
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={validData}
              margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey={xAxisKey}
                tick={{ fontSize: 12 }}
                interval={0}
                angle={-45}
                textAnchor="end"
                tickFormatter={(value) => truncate(value, 11)}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value, name, props) => [
                  value,
                  `${props.payload[xAxisKey]}: ${getValueLabel(viewType)}`,
                ]}
                cursor={{ strokeDasharray: "3 3" }}
              />
              <Line
                type="monotone"
                dataKey={dataKey}
                stroke="#0088FE"
                strokeWidth={3}
                activeDot={{ r: 8 }}
                dot={{ r: 5 }}
                animationDuration={800}
              />
            </LineChart>
          </ResponsiveContainer>
        );
    }
  };

  const createBoldHeading = (text) => {
    return <span style={{ fontWeight: 600, fontSize: "13px" }}>{text}</span>;
  };

  // Add these color generation functions before renderChartByType
  const generateRandomColor = () => {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  const generateColorPalette = (count, viewType) => {
    // Define different color palettes for each view type
    const colorPalettes = {
      sessions: [
        "#0088FE",
        "#00C49F",
        "#FFBB28",
        "#FF8042",
        "#8884d8",
        "#82ca9d",
        "#ffc658",
        "#ff7c7c",
        "#8dd1e1",
        "#a4de6c",
      ],
      engagement: [
        "#6050DC",
        "#D52DB7",
        "#FF2E7E",
        "#FF6B45",
        "#FFAB05",
        "#00D9FF",
        "#7B61FF",
        "#FF1744",
        "#00E676",
        "#FFC400",
      ],
      products: [
        "#E91E63",
        "#9C27B0",
        "#673AB7",
        "#3F51B5",
        "#2196F3",
        "#00BCD4",
        "#009688",
        "#4CAF50",
        "#8BC34A",
        "#CDDC39",
      ],
    };

    const basePalette = colorPalettes[viewType] || colorPalettes.sessions;

    // If we need more colors than available in the palette, generate random ones
    if (count <= basePalette.length) {
      return basePalette.slice(0, count);
    }

    // Generate additional random colors if needed
    const colors = [...basePalette];
    while (colors.length < count) {
      colors.push(generateRandomColor());
    }

    return colors;
  };

  const renderCard = (cardId) => {
    switch (cardId) {
      case "blogOverview":
        return (
          <LegacyCard
            title="Blog Overview"
            sectioned
            className="dashboard-stat-card"
          >
            <div className="dashboard-stat-content">
              <div className="count-display">
                <div className="total-count-circle">
                  <svg className="progress-ring" viewBox="0 0 100 100">
                    <circle
                      className="progress-ring__background"
                      cx="50"
                      cy="50"
                      r={radius}
                    />
                    <circle
                      className="progress-ring__progress"
                      cx="50"
                      cy="50"
                      r={radius}
                    />
                  </svg>
                  <div className="total-count-number">{totalCount}</div>
                </div>
              </div>
              <div className="blog-overview-wrapper">
                <div className="publish_unpublish">
                  <div className="status-option">
                    <RadioButton
                      label="Published"
                      checked={selectedStatus === "published"}
                      id="published"
                      name="blogStatus"
                      onChange={() => setSelectedStatus("published")}
                    />
                    <span className="status-count">{blogCounts.published}</span>
                  </div>
                  <div className="status-option">
                    <RadioButton
                      label="Draft"
                      checked={selectedStatus === "draft"}
                      id="draft"
                      name="blogStatus"
                      onChange={() => setSelectedStatus("draft")}
                    />
                    <span className="status-count">
                      {blogCounts.unpublished}
                    </span>
                  </div>
                </div>
              </div>
              <Button
                variant="plain"
                onClick={() =>
                  navigate(
                    `/all-blogs-listing?shop=${encodeURIComponent(
                      shopOrigin
                    )}&status=${selectedStatus}`
                  )
                }
              >
                See All Articles
              </Button>
            </div>
          </LegacyCard>
        );

      case "customerSupport":
        return (
          <LegacyCard
            title="Customer Support"
            sectioned
            className="dashboard-stat-card card3"
          >
            <div className="dashboard-stat-content card3">
              <Text as="p">
                Find quick answers in detailed tutorials and frequently asked
                questions.
              </Text>
            </div>
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                justifyContent: "space-between",
              }}
            >
              {/* Share Feedback Button - ALWAYS visible, ALWAYS works */}
              
              <div>
                <Button
                  variant="plain"
                  onClick={async () => {
                    const triggerFn = reviewTrigger || triggerReview;
                    
                    try {
                      await triggerFn();
                    } catch (err) {
                      console.error("Error triggering review:", err);
                    }
                  }}
                >
                  Share Feedback
                </Button>
              </div>
              <div>
                <Button variant="plain" onClick={openWidget}>
                  Talk to Us
                </Button>
              </div>
              <div>
                <Link
                  target="_blank"
                  to="https://bloghero.freshdesk.com/support/solutions/206000038702"
                >
                  <Button variant="plain">FAQ</Button>
                </Link>
              </div>
            </div>
          </LegacyCard>
        );

      case "performance":
        return (
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h3" variant="headingMd">
                  🚀 Performance Insights{" "}
                </Text>
                {/* {insightsLoading && <Spinner size="small" />} */}
              </InlineStack>
              {insightsLoading ? (
                <div
                  style={{
                    minHeight: "180px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Spinner size="small" />
                  <Text as="p" variant="bodySm" tone="subdued">
                    Analyzing your blog data…
                  </Text>
                </div>
              ) : (
                <BlockStack gap="400">
                  {insights.performance_insights &&
                  insights.performance_insights.length > 0 ? (
                    insights.performance_insights.map((insight, index) => (
                      <Text
                        as="p"
                        variant="bodySm"
                        key={insight.id || `perf-${index}`}
                      >
                        {insight.parts && Array.isArray(insight.parts) ? (
                          insight.parts.map((part, pIndex) =>
                            typeof part === "string" ? (
                              part
                            ) : part.type === "link" ? (
                              <Link
                                key={pIndex}
                                className="Polaris-Text--root Polaris-Text--bodySm"
                                to={`/edit-blogs?shop=${encodeURIComponent(
                                  shopOrigin
                                )}&article_id=${encodeURIComponent(
                                  part.article_id || ""
                                )}`}
                                style={{
                                  color: "#303030",
                                  textDecoration: "none",
                                  cursor: "pointer",
                                }}
                              >
                                {part.text}
                              </Link>
                            ) : null
                          )
                        ) : (
                          <Text as="span" tone="subdued">
                            No Insights data available. Browse Article.
                          </Text>
                        )}
                      </Text>
                    ))
                  ) : (
                    <Text as="p" variant="bodySm" tone="subdued">
                      No insights available yet. Keep creating content!
                    </Text>
                  )}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        );

      case "optimization":
        return (
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h3" variant="headingMd">
                  ✍️ Content Optimization
                </Text>
                {/* {insightsLoading && <Spinner size="small" />} */}
              </InlineStack>
              {insightsLoading ? (
                <div
                  style={{
                    minHeight: "180px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Spinner size="small" />
                  <Text as="p" variant="bodySm" tone="subdued">
                    Analyzing your blog data…
                  </Text>
                </div>
              ) : (
                <BlockStack gap="400">
                  {insights.content_optimization &&
                  insights.content_optimization.length > 0 ? (
                    insights.content_optimization.map((insight, index) => (
                      <Text
                        as="p"
                        variant="bodySm"
                        key={insight.id || `opt-${index}`}
                      >
                        {insight.parts && Array.isArray(insight.parts) ? (
                          insight.parts.map((part, pIndex) =>
                            typeof part === "string" ? (
                              part
                            ) : part.type === "link" ? (
                              <Link
                                key={pIndex}
                                className="Polaris-Text--root Polaris-Text--bodySm"
                                to={`/edit-blogs?shop=${encodeURIComponent(
                                  shopOrigin
                                )}&article_id=${encodeURIComponent(
                                  part.article_id || ""
                                )}`}
                                style={{
                                  color: "#303030",
                                  textDecoration: "none",
                                  cursor: "pointer",
                                }}
                              >
                                {part.text}
                              </Link>
                            ) : null
                          )
                        ) : (
                          <Text as="span" tone="subdued">
                            No Insights data available. Browse Article.
                          </Text>
                        )}
                      </Text>
                    ))
                  ) : (
                    <Text as="p" variant="bodySm" tone="subdued">
                      No insights available yet. Keep creating content!
                    </Text>
                  )}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        );

      case "strategic":
        return (
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h3" variant="headingMd">
                  ✅ Strategic & Conversion Actions
                </Text>
                {/* {insightsLoading && <Spinner size="small" />} */}
              </InlineStack>
              {insightsLoading ? (
                <div
                  style={{
                    minHeight: "180px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Spinner size="small" />
                  <Text as="p" variant="bodySm" tone="subdued">
                    Analyzing your blog data…
                  </Text>
                </div>
              ) : (
                <BlockStack gap="400">
                  {insights.strategic_actions &&
                  insights.strategic_actions.length > 0 ? (
                    insights.strategic_actions.map((insight, index) => (
                      <Text
                        as="p"
                        variant="bodySm"
                        key={insight.id || `strat-${index}`}
                      >
                        {insight.parts && Array.isArray(insight.parts) ? (
                          insight.parts.map((part, pIndex) =>
                            typeof part === "string" ? (
                              part
                            ) : part.type === "link" ? (
                              <Link
                                key={pIndex}
                                className="Polaris-Text--root Polaris-Text--bodySm"
                                to={`/edit-blogs?shop=${encodeURIComponent(
                                  shopOrigin
                                )}&article_id=${encodeURIComponent(
                                  part.article_id || ""
                                )}`}
                                style={{
                                  color: "#303030",
                                  textDecoration: "none",
                                  cursor: "pointer",
                                }}
                              >
                                {part.text}
                              </Link>
                            ) : null
                          )
                        ) : (
                          <Text as="span" tone="subdued">
                            No Insights data available. Browse Article.
                          </Text>
                        )}
                      </Text>
                    ))
                  ) : (
                    <Text as="p" variant="bodySm" tone="subdued">
                      No insights available yet. Keep creating content!
                    </Text>
                  )}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        );

      case "sessions":
        // const isLimitedPlan = userPlan === "free" || userPlan === "basic";

        return (
          <Card>
            <BlockStack gap="300">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text as="h3" variant="headingMd">
                  {/* {isLimitedPlan
                    ? "Top 3 Blog Posts by Sessions"
                    : `Top ${
                        topPostsSessionsCount !== "All"
                          ? topPostsSessionsCount
                          : ""
                      } Blog Posts by Sessions`} */}
                      All Blog Posts by Sessions
                </Text>
                <DateRangePicker
                  initialDateRange={{
                    title: "Last 30 days",
                    alias: "last30days",
                    period: dateRangeSessions,
                  }}
                  onChange={(period) => setDateRangeSessions(period)}
                />
              </div>

              {sessionLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "40px",
                  }}
                >
                  <Spinner size="large" />
                </div>
              ) : topPostsBySessions.length === 0 ? (
                <Text as="p" tone="subdued">
                  No session data available for the selected period.
                </Text>
              ) : (
                <>
                  <DataTable
                    columnContentTypes={["text", "numeric"]}
                    headings={[
                      createBoldHeading("Article Title"),
                      createHeaderWithTooltip_session(
                        "Views",
                        "Total number of page views this article has received"
                      ),
                    ]}
                    rows={topPostsBySessions}
                  />

                  {/* {isLimitedPlan && (
                    <div
                      style={{
                        borderTop: "1px solid #E1E3E5",
                        paddingTop: "12px",
                        marginTop: "8px",
                      }}
                    >
                      <Text
                        as="p"
                        variant="bodyMd"
                        tone="caution"
                        alignment="center"
                      >
                        View detailed analytics in BlogHero Pro.{" "}
                        <Link
                          to={`/subscription?shop=${encodeURIComponent(
                            shopOrigin || ""
                          )}`}
                        >
                          Upgrade Plan?
                        </Link>
                      </Text>
                    </div>
                  )} */}

                  {/* {!isLimitedPlan && ( */}
                    <div
                      style={{
                        borderTop: "1px solid #E1E3E5",
                        padding: "16px 0 0",
                        marginTop: "19px",
                      }}
                    >
                      <InlineStack align="center">
                        <Popover
                          active={sessionsCountPopoverActive}
                          activator={
                            <Button
                              onClick={() =>
                                setSessionsCountPopoverActive(
                                  !sessionsCountPopoverActive
                                )
                              }
                              disclosure
                            >
                              Show{" "}
                              {topPostsSessionsCount === "All"
                                ? "All"
                                : `Top ${topPostsSessionsCount}`}
                            </Button>
                          }
                          onClose={() => setSessionsCountPopoverActive(false)}
                        >
                          <Popover.Pane>
                            <OptionList
                              options={[
                                { label: "Top 5", value: "5" },
                                { label: "Top 10", value: "10" },
                                { label: "All", value: "All" },
                              ]}
                              selected={topPostsSessionsCount}
                              onChange={(value) => {
                                if (value[0] === "10" || value[0] === "All") {
                                  navigate(
                                    `/all-posts?shop=${encodeURIComponent(
                                      shopOrigin
                                    )}&type=sessions&limit=${
                                      value[0]
                                    }&since=${dateRangeSessions.since.toISOString()}&until=${dateRangeSessions.until.toISOString()}`
                                  );
                                } else {
                                  setTopPostsSessionsCount(value[0]);
                                }
                                setSessionsCountPopoverActive(false);
                              }}
                            />
                          </Popover.Pane>
                        </Popover>
                      </InlineStack>
                    </div>
                  {/* )} */}
                </>
              )}
            </BlockStack>
          </Card>
        );

      case "engagement":
        // const isLimitedPlanEng = userPlan === "free" || userPlan === "basic";

        return (
          <Card>
            <BlockStack gap="300">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text as="h3" variant="headingMd">
                  {/* {isLimitedPlanEng
                    ? "Top 3 Blog Posts by Engagement"
                    : `Top ${
                        topPostsEngagementsCount !== "All"
                          ? topPostsEngagementsCount
                          : ""
                      } Blog Posts by Engagement`} */}
                      All Blog Posts by Engagement
                </Text>
                <DateRangePicker
                  initialDateRange={{
                    title: "Last 30 days",
                    alias: "last30days",
                    period: dateRangeEngagement,
                  }}
                  onChange={(period) => setDateRangeEngagement(period)}
                />
              </div>

              {engagementLoading ? (
                <div
                  style={{
                    minHeight: "250px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Spinner size="large" />
                  <Text as="p" variant="bodySm" tone="subdued">
                    Analyzing your blog data…
                  </Text>
                </div>
              ) : topPostsByEngagement.length === 0 ? (
                <Text as="p" tone="subdued">
                  No engagement data available for the selected period.
                </Text>
              ) : (
                <>
                  <DataTable
                    columnContentTypes={["text", "numeric"]}
                    headings={[
                      createBoldHeading("Article Title"),
                      createHeaderWithTooltip(
                        "Engagement",
                        "Engagement rate based on average time spent reading the article"
                      ),
                    ]}
                    rows={topPostsByEngagement}
                  />

                  {/* {isLimitedPlanEng && (
                    <div
                      style={{
                        borderTop: "1px solid #E1E3E5",
                        paddingTop: "12px",
                        marginTop: "8px",
                      }}
                    >
                      <Text
                        as="p"
                        variant="bodyMd"
                        tone="caution"
                        alignment="center"
                      >
                        View detailed analytics in BlogHero Pro.{" "}
                        <Link
                          to={`/subscription?shop=${encodeURIComponent(
                            shopOrigin || ""
                          )}`}
                        >
                          Upgrade Plan?
                        </Link>
                      </Text>
                    </div>
                  )} */}

                  {/* {!isLimitedPlanEng && ( */}
                    <div
                      style={{
                        borderTop: "1px solid #E1E3E5",
                        padding: "16px 0 0",
                      }}
                    >
                      <InlineStack align="center">
                        <Popover
                          active={engagementCountPopoverActive}
                          activator={
                            <Button
                              onClick={() =>
                                setEngagementCountPopoverActive(
                                  !engagementCountPopoverActive
                                )
                              }
                              disclosure
                            >
                              Show{" "}
                              {topPostsEngagementsCount === "All"
                                ? "All"
                                : `Top ${topPostsEngagementsCount}`}
                            </Button>
                          }
                          onClose={() => setEngagementCountPopoverActive(false)}
                        >
                          <Popover.Pane>
                            <OptionList
                              options={[
                                { label: "Top 5", value: "5" },
                                { label: "Top 10", value: "10" },
                                { label: "All", value: "All" },
                              ]}
                              selected={topPostsEngagementsCount}
                              onChange={(value) => {
                                if (value[0] === "10" || value[0] === "All") {
                                  navigate(
                                    `/all-posts?shop=${encodeURIComponent(
                                      shopOrigin
                                    )}&type=engagement&limit=${
                                      value[0]
                                    }&since=${dateRangeEngagement.since.toISOString()}&until=${dateRangeEngagement.until.toISOString()}`
                                  );
                                } else {
                                  setTopPostsEngagementsCount(value[0]);
                                }
                                setEngagementCountPopoverActive(false);
                              }}
                            />
                          </Popover.Pane>
                        </Popover>
                      </InlineStack>
                    </div>
                  {/* )} */}
                </>
              )}
            </BlockStack>
          </Card>
        );

      case "products":
        return (
          <Card>
            <BlockStack gap="300">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text as="h3" variant="headingMd">
                  Top 5 Clicked Products
                </Text>
                <DateRangePicker
                  initialDateRange={{
                    title: "Last 30 days",
                    alias: "last30days",
                    period: dateRangeProducts,
                  }}
                  onChange={(period) => setDateRangeProducts(period)}
                />
              </div>
              {productsLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "40px",
                  }}
                >
                  <Spinner size="large" />
                </div>
              ) : topClickedProducts.length > 0 ? (
                <DataTable
                  columnContentTypes={["text", "numeric"]}
                  headings={[
                    createBoldHeading("Product Title"),
                    createHeaderWithTooltip_product(
                      "Clicks",
                      "Total number of clicks on this product from blog articles"
                    ),
                  ]}
                  rows={topClickedProducts}
                />
              ) : (
                <Text as="p" tone="subdued">
                  No product click data available for the selected period.
                </Text>
              )}
            </BlockStack>
          </Card>
        );

      
      case "charts":
        const limitValue =
          topPostsSessionsCount === "All"
            ? chartData.length
            : parseInt(topPostsSessionsCount);

        // ✅ Determine which data to display based on selected view
        let displayData = [];
        let displayLoading = false;
        let displayDataKey = "views";
        let displayTitle = "Charts by session";

        switch (selectedChartView) {
          case "sessions":
            displayData = chartData.slice(0, limitValue);
            displayLoading = chartsLoading;
            displayDataKey = "views";
            displayTitle = `Top ${
              topPostsSessionsCount !== "All" ? topPostsSessionsCount : ""
            } Blog Posts by Sessions`;
            break;
            
          case "engagement":
            // ✅ FIXED: Extract engagement data correctly from table rows
            displayData = topPostsByEngagement.map((row) => {
              // Extract title from Link component
              const titleElement = row[0];
              let title = "Untitled";
              
              if (titleElement?.props?.children) {
                title = titleElement.props.children;
              }
              
              // Extract engagement percentage from nested Text component
              const engagementElement = row[1];
              let engagement = 0;
              
              if (engagementElement?.props?.children?.props?.children?.props?.children) {
                // This is the Text component with the percentage
                const engagementText = engagementElement.props.children.props.children.props.children;
                engagement = parseInt(engagementText) || 0;
              }
              
              return {
                title,
                engagement,
              };
            });
            displayLoading = engagementLoading;
            displayDataKey = "engagement";
            displayTitle = `Top ${
              topPostsEngagementsCount !== "All" ? topPostsEngagementsCount : ""
            } Blog Posts by Engagement`;
            break;
            
          case "products":
            // ✅ FIXED: Extract product data correctly from table rows
            displayData = topClickedProducts.map((row) => {
              // Extract product title (plain string)
              const title = row[0] || "Untitled";
              
              // Extract clicks from nested Text component
              const clicksElement = row[1];
              let clicks = 0;
              
              if (clicksElement?.props?.children?.props?.children?.props?.children) {
                // This is the Text component with the click count
                const clicksText = clicksElement.props.children.props.children.props.children;
                clicks = parseInt(clicksText) || 0;
              }
              
              return {
                title,
                clicks,
              };
            });
            displayLoading = productsLoading;
            displayDataKey = "clicks";
            displayTitle = "Top 5 Clicked Products";
            break;
            
          default:
            displayData = chartData.slice(0, limitValue);
            displayLoading = chartsLoading;
            displayDataKey = "views";
        }

        return (
          <Card>
            <BlockStack gap="300">
              {/* Header with dropdowns */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <Text as="h3" variant="headingMd">
                  {displayTitle}
                </Text>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {/* View Type Dropdown */}
                  <Popover
                    active={chartViewPopoverActive}
                    activator={
                      <Button
                        onClick={() =>
                          setChartViewPopoverActive(!chartViewPopoverActive)
                        }
                        disclosure
                      >
                        {selectedChartView.charAt(0).toUpperCase() +
                          selectedChartView.slice(1)}
                      </Button>
                    }
                    onClose={() => setChartViewPopoverActive(false)}
                  >
                    <Popover.Pane>
                      <OptionList
                        options={[
                          { label: "Sessions", value: "sessions" },
                          { label: "Engagement", value: "engagement" },
                          { label: "Products", value: "products" },
                        ]}
                        selected={[selectedChartView]}
                        onChange={(value) => {
                          setSelectedChartView(value[0]);
                          setChartViewPopoverActive(false);
                        }}
                      />
                    </Popover.Pane>
                  </Popover>

                  {/* Chart Type Dropdown */}
                  <Popover
                    active={chartTypePopoverActive}
                    activator={
                      <Button
                        onClick={() =>
                          setChartTypePopoverActive(!chartTypePopoverActive)
                        }
                        disclosure
                      >
                        {chartsChartType.charAt(0).toUpperCase() +
                          chartsChartType.slice(1)}
                      </Button>
                    }
                    onClose={() => setChartTypePopoverActive(false)}
                  >
                    <Popover.Pane>
                      <OptionList
                        options={[
                          { label: "Line", value: "line" },
                          { label: "Bar", value: "bar" },
                          { label: "Pie", value: "pie" },
                        ]}
                        selected={[chartsChartType]}
                        onChange={(value) => {
                          setChartsChartType(value[0]);
                          setChartTypePopoverActive(false);
                        }}
                      />
                    </Popover.Pane>
                  </Popover>

                  {/* Date Range Picker */}
                  <DateRangePicker
                    initialDateRange={{
                      title: "Last 30 days",
                      alias: "last30days",
                      period: dateRangeCharts,
                    }}
                    onChange={(period) => setDateRangeCharts(period)}
                  />
                </div>
              </div>

              {/* Chart Display */}
              {displayLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "40px",
                  }}
                >
                  <Spinner size="large" />
                </div>
              ) : displayData.length > 0 ? (
                renderChartByType(
                  chartsChartType,
                  displayData,
                  displayDataKey,
                  "title",
                  displayTitle,
                  selectedChartView
                )
              ) : (
                <Text as="p" tone="subdued">
                  No chart data available for the selected period.
                </Text>
              )}
            </BlockStack>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <Page>
        <style>{customStyles}</style>
        {/* Review Manager - handles automatic & manual review prompts */}
        <ReviewManager
          shopOrigin={shopOrigin}
          onTriggerAvailable={(fn) => setReviewTrigger(() => fn)}
        />
          <DndContext
            sensors={sensors}
            collisionDetection={customCollisionDetection}
            onDragEnd={handleDragEnd}
          >
            <div className="dashboard-header">
              <Box>
                <Text
                  className="shop_owner_name"
                  variant="heading2xl"
                  as="h2"
                  alignment="center"
                >
                  <span style={{ textTransform: "capitalize" }}>
                    Welcome, {shopOwnerName || "User"}
                  </span>
                </Text>
              </Box>
              <div style={{ marginTop: "15px" }}>
                <Text alignment="center" variant="bodyLg" as="p" tone="subdued">
                  This is your AI blog dashboard, start or edit your content
                  here.
                </Text>
              </div>
            </div>
            {/* Edit toggle */}
            <div className="edit_dashboard_div">
              <Button
                onClick={() => navigateToneSetup()}
                variant="primary"
                icon={MagicIcon}
                size="medium"
              >
                Create content with AI
              </Button>
              {editMode && (
                <Button onClick={handleResetLayout} icon={ResetIcon}>
                  Reset
                </Button>
              )}
              <Button
                icon={EditIcon}
                variant={editMode ? "primary" : "primary"}
                onClick={() => setEditMode((v) => !v)}
              >
                {editMode ? "Done" : "Edit Dashboard"}
              </Button>
            </div>

            {/* AI Promo Banner */}
            {showPromoBanner && (
              <div style={{ marginTop: "16px" }}>
                <div
                  style={{
                    background: "var(--p-color-bg-surface)",
                    border: "1px solid var(--p-color-border)",
                    borderRadius: "var(--p-border-radius-300)",
                    padding: "16px 20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    boxShadow: "var(--p-shadow-100)",
                    position: "relative",
                  }}
                >
                  {/* NEW badge + Icon */}
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <div
                      style={{
                        width: "72px",
                        height: "72px",
                        borderRadius: "16px",
                        background: "linear-gradient(135deg, #7B8FD4 0%, #5C6BC0 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "36px",
                      }}
                    >
                      <img src="https://bloghero.io/static/AnalyticsHeroIcon.png" alt="HeroIcon" width="72px" height="72px" />
                    </div>
                    <div
                      style={{
                        position: "absolute",
                        top: "-13px",
                        left: "-12px",
                        background: "#54ab58",
                        color: "#fff",
                        fontSize: "8px",
                        fontWeight: "600",
                        padding: "1px 6px",
                        borderRadius: "6px",
                        letterSpacing: "1px",
                      }}
                    >
                      NEW
                    </div>
                  </div>

                  {/* Text content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text variant="headingMd" as="p" fontWeight="bold">
                      AnalyticsHero - Blog Analytics
                    </Text>
                    <div style={{ marginTop: "4px" }}>
                      <Text variant="bodySm" as="p" tone="subdued">
                        Find out which blog posts actually drive sales - not just views. Free install, results in minutes.
                      </Text>
                    </div>

                    {/* Dots indicator
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        marginTop: "10px",
                        alignItems: "center",
                      }}
                    >
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          style={{
                            width: i === 0 ? "10px" : "8px",
                            height: i === 0 ? "10px" : "8px",
                            borderRadius: "50%",
                            background:
                              i === 0
                                ? "var(--p-color-icon-interactive)"
                                : "var(--p-color-border)",
                          }}
                        />
                      ))}
                    </div> */}
                  </div>

                  {/* CTA Button */}
                  <div style={{ flexShrink: 0 }}>
                    <Button
                      variant="secondary"
                      onClick={() => navigateFreeInstall()}
                    >
                      Free Install
                    </Button>
                  </div>

                  {/* Dismiss X button */}
                  <div style={{ position: "absolute", top: "10px", right: "10px" }}>
                    <Button
                      variant="plain"
                      icon={XSmallIcon}
                      accessibilityLabel="Dismiss banner"
                      onClick={() => {
                        setShowPromoBanner(false);
                        try {
                          localStorage.setItem("ai_promo_banner_dismissed", "true");
                        } catch {}
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            <SortableContext
              items={gridLayout.map((item) => item.id)}
              strategy={rectSortingStrategy}
            >
              <div className="dashboard-grid-container">
                {gridLayout.map((item) => (
                  <SortableCard
                    key={item.id}
                    id={item.id}
                    editMode={editMode}
                    draggingHeight={draggingHeight}
                    style={{
                      gridColumn: `span ${
                        item.adjustedColSpan || item.colSpan
                      }`,
                    }}
                  >
                    {renderCard(item.id)}
                  </SortableCard>
                ))}
              </div>
            </SortableContext>

            {/* Create content with AI */}
            <div className="dashboard-section-header">
              <div className="dashboard-create-button">
                <Button
                  onClick={() => navigateToneSetup()}
                  variant="primary"
                  icon={MagicIcon}
                  size="medium"
                >
                  Create content with AI
                </Button>
              </div>
            </div>

            {/* Recent Posts */}
            <div className="dashboard-table-container">
              <Card padding="0">
                <div className="dashboard-section-header-recent">
                  <div className="dashboard-section-title">
                    <Text variant="headingMd" as="p">
                      Recent Posts
                    </Text>
                  </div>
                </div>

                <IndexTable
                  selectable={false}
                  resourceName={{ singular: "article", plural: "articles" }}
                  itemCount={loading ? 3 : articles.length}
                  headings={[
                    { title: "Title" },
                    { title: "Status" },
                    { title: "Category" },
                    { title: "Last Modified" },
                    { title: "Actions" },
                  ]}
                  loading={loading}
                >
                  {loading
                    ? [0, 1, 2].map((i) => (
                        <IndexTable.Row id={`skeleton-${i}`} key={`skeleton-${i}`} position={i}>
                          <IndexTable.Cell>
                            <div style={{ height: "16px", background: "#f1f1f1", borderRadius: "4px", width: "60%" }} />
                          </IndexTable.Cell>
                          <IndexTable.Cell>
                            <div style={{ height: "16px", background: "#f1f1f1", borderRadius: "4px", width: "60px" }} />
                          </IndexTable.Cell>
                          <IndexTable.Cell>
                            <div style={{ height: "16px", background: "#f1f1f1", borderRadius: "4px", width: "80px" }} />
                          </IndexTable.Cell>
                          <IndexTable.Cell>
                            <div style={{ height: "16px", background: "#f1f1f1", borderRadius: "4px", width: "100px" }} />
                          </IndexTable.Cell>
                          <IndexTable.Cell>
                            <div style={{ height: "16px", background: "#f1f1f1", borderRadius: "4px", width: "80px" }} />
                          </IndexTable.Cell>
                        </IndexTable.Row>
                      ))
                    : rowMarkup}
                </IndexTable>
              </Card>
            </div>
            <div className="dashboard-see-all-container">
              <Button
                variant="secondary"
                onClick={() =>
                  navigate(
                    `/all-blogs-listing?shop=${encodeURIComponent(shopOrigin)}`
                  )
                }
              >
                See All Posts
              </Button>
            </div>
            <div className="dashboard_article_week">
              <Text variant="bodySm" as="p">
                {publishedThisWeek} of {totalThisWeek} article
                {totalThisWeek !== 1 ? "s" : ""} Published this week 💪{" "}
              </Text>
              <div style={{ width: 100 }}>
                <ProgressBar
                  progress={(publishedThisWeek / totalThisWeek) * 100 || 0}
                  size="small"
                  tone="primary"
                />
              </div>
            </div>
          </DndContext>
          <ArticleModal
            open={modalOpen}
            onClose={() => {
              setModalOpen(false);
              setSelectedArticle(null);
            }}
            article={selectedArticle}
          />
          <DeleteArticleModal
            open={deleteModalOpen}
            onClose={() => {
              setDeleteModalOpen(false);
              setSelectedArticle(null);
            }}
            article={selectedArticle}
            shopOrigin={shopOrigin}
            onDeleteSuccess={() => {
              setBanner({
                show: true,
                message: "Article deleted",
                status: "success",
              });
              fetchBlogCounts();
              fetchArticles();
            }}
          />
          <UpdateStatusModal
            open={statusModalOpen}
            onClose={() => {
              setStatusModalOpen(false);
              setSelectedArticle(null);
            }}
            article={selectedArticle}
            shopOrigin={shopOrigin}
            onStatusUpdateSuccess={() => {
              setBanner({
                show: true,
                message: "Status updated",
                status: "success",
              });
              fetchBlogCounts();
              fetchArticles();
            }}
          />
          <GeneratedArticleModal
            open={modalOpen && generatedArticle !== null}
            onClose={() => {
              setModalOpen(false);
              setSelectedArticle(null);
              setGeneratedArticle(null);
            }}
            article={generatedArticle}
            shopOrigin={shopOrigin}
          />
          <OptimisedArticleModal
            open={optimisedArticleModalOpen}
            onClose={() => {
              setOptimisedArticleModalOpen(false);
              setOptimisedArticlePreview(null);
            }}
            onConfirm={handleConfirmOptimise}
            optimisedArticle={optimisedArticlePreview}
            isConfirming={isConfirmingOptimise}
          />
          <Modal
            open={isDateModalOpen}
            onClose={() => setIsDateModalOpen(false)}
            title="Set visibility date"
            primaryAction={{
              content: "Set visibility date",
              onAction: handleSetVisibilityDate,
            }}
            secondaryActions={[
              {
                content: "Cancel",
                onAction: () => setIsDateModalOpen(false),
              },
            ]}
          >
            <Modal.Section>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  {new Date(year, month).toLocaleString("en-US", {
                    month: "long",
                  })}
                </div>
                <DatePicker
                  month={month}
                  year={year}
                  onChange={setSelectedDates}
                  onMonthChange={(m, y) => setDate({ month: m, year: y })}
                  selected={selectedDates}
                />
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <div style={{ width: "100%" }}>
                    <TextField
                      label="Time"
                      type="time"
                      value={scheduledTime}
                      onChange={setScheduledTime}
                    />
                  </div>
                </div>
              </div>
            </Modal.Section>
          </Modal>
        </Page>
      {toastActive && (
        <Toast
          content={toastContent}
          onDismiss={() => setToastActive(false)}
          error={toastError}
        />
      )}
    </div>
  );
}

export default Dashboard;